import { Pencil, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../../../components/ui/common/button";
import SalesUnitImageUpload from "../../shared/components/SalesUnitImageUpload";
import {
  getSalesUnitImage,
  readFileAsDataUrl,
  removeSalesUnitImage,
  setSalesUnitImage
} from "../../shared/salesUnitImageStorage";
import { SalesUnit } from "../types/productEditView.types";

interface ProductUnitTableProps {
  units: SalesUnit[];
  onEdit: (unit: SalesUnit) => void;
  onDelete: (unit: SalesUnit) => void;
}

/**
 * A product's existing unit sizes, one bordered card per unit — matches the
 * Lovable prototype's "Sales unit N" card layout in
 * `RegisterProductDialog.tsx`, rather than a plain table row. A sibling of,
 * but not shared with, `productRegister/components/ProductUnitTable.tsx` —
 * that one's delete action removes an unsaved local row by index, this one
 * deletes a persisted sales unit by `_id` via the server, so the callback
 * shapes differ.
 *
 * The image control is client-side only (localStorage, keyed by the unit's
 * real `_id`) — the real API has no per-unit image field. Only offered
 * here, not in the register flow's cards, because a unit only gets a real
 * `_id` once it's been saved — exactly what this list always shows.
 */
const ProductUnitTable = ({ units, onEdit, onDelete }: ProductUnitTableProps) => {
  const [imagesByUnitId, setImagesByUnitId] = useState<Record<string, string | null>>({});

  useEffect(() => {
    const next: Record<string, string | null> = {};
    units.forEach(unit => {
      if (unit._id) next[unit._id] = getSalesUnitImage(unit._id);
    });
    setImagesByUnitId(next);
  }, [units]);

  const handleUpload = async (unitId: string, file: File) => {
    const dataUrl = await readFileAsDataUrl(file);
    setSalesUnitImage(unitId, dataUrl);
    setImagesByUnitId(prev => ({ ...prev, [unitId]: dataUrl }));
  };

  const handleRemove = (unitId: string) => {
    removeSalesUnitImage(unitId);
    setImagesByUnitId(prev => ({ ...prev, [unitId]: null }));
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {units.map((unit, index) => (
        <div
          key={unit._id ?? index}
          className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-3"
        >
          <div className="tw-flex tw-items-center tw-justify-between">
            <p className="tw-font-mono tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground">
              Sales unit {index + 1}
            </p>
            <div className="tw-flex tw-items-center tw-gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="tw-h-7 tw-w-7"
                title="Edit"
                onClick={() => onEdit(unit)}
              >
                <Pencil className="tw-h-3.5 tw-w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="tw-h-7 tw-w-7 tw-text-destructive hover:tw-text-destructive"
                title="Delete"
                onClick={() => onDelete(unit)}
              >
                <Trash2 className="tw-h-3.5 tw-w-3.5" />
              </Button>
            </div>
          </div>

          <div className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-4">
            <div>
              <p className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                Unit Name
              </p>
              <p className="tw-text-sm tw-text-foreground">{unit.sales_unit_name || "-"}</p>
            </div>
            <div>
              <p className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                Unit Size
              </p>
              <p className="tw-font-mono tw-text-sm tw-text-foreground">
                {unit.sales_unit_size ?? "-"}
              </p>
            </div>
            <div>
              <p className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                Unit Type
              </p>
              <p className="tw-font-mono tw-text-sm tw-text-foreground">
                {unit.sales_unit_type || "-"}
              </p>
            </div>
            <div>
              <p className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                EAN Barcode
              </p>
              <p className="tw-font-mono tw-text-sm tw-text-foreground">
                {unit.ean_barcode || "-"}
              </p>
            </div>
          </div>

          {unit._id ? (
            <div className="tw-mt-3">
              <SalesUnitImageUpload
                imageUrl={imagesByUnitId[unit._id] ?? null}
                onUpload={file => handleUpload(unit._id as string, file)}
                onRemove={() => handleRemove(unit._id as string)}
              />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default ProductUnitTable;
